import AsyncStorage from '@react-native-async-storage/async-storage';
import Constants from 'expo-constants';
import { AppState, AppStateStatus, Platform } from 'react-native';
import { focusManager, QueryClient as $QueryClient } from 'react-query';
import { createAsyncStoragePersistor } from 'react-query/createAsyncStoragePersistor-experimental';
import { persistQueryClient } from 'react-query/persistQueryClient-experimental';

import useStore from 'app/hooks/useStore';
import Logger from 'app/services/Logger';
import { toMs } from 'app/utils/time';

let instance: $QueryClient;

export enum QueryKeys {
  GuestData = 'getUserData',
  GuestDataByLocator = 'getUserDataByLocator',
  Configuration = 'getConfiguration',
  VerifyEmail = 'verifyEmail',
  VerifyCode = 'verifyCode',
  AddToWallet = 'addToWallet',
  ReservationTimes = 'getReservationTimes',
  ReservationDays = 'getReservationDays',
}

export default function QueryClient() {
  if (instance) return instance;

  const queryClient = new $QueryClient({
    defaultOptions: {
      queries: {
        staleTime: toMs(5, 'm'),
        cacheTime: toMs(10, 'm'),
        refetchOnWindowFocus: true,
        retry: 5,
        retryDelay: (attemptIdx) =>
          Math.min(Math.floor(1000 * 2 ** attemptIdx * (Math.random() + 0.8)), toMs(30, 's')),
      },
    },
  });

  // https://react-query.tanstack.com/plugins/createAsyncStoragePersistor
  const asyncStoragePersistor = createAsyncStoragePersistor({
    storage: AsyncStorage,
  });

  // https://react-query.tanstack.com/plugins/persistQueryClient
  persistQueryClient({
    queryClient,
    persistor: asyncStoragePersistor,
    buster: Constants.expoConfig?.version || 'dev',
  });

  if (Platform.OS !== 'web') {
    focusManager.setEventListener((handleFocus) => {
      const handler = (state: AppStateStatus) => {
        Logger.debug('[QueryClient] app state change', { state });

        if (state === 'active') {
          const { email, user } = useStore.getState();
          const isCitypassUser =
            email?.endsWith('@citypass.com') || user?.email?.endsWith('@citypass.com');

          if (isCitypassUser) {
            handleFocus(true);
          } else {
            const queryCache = queryClient.getQueryCache();
            const queries = queryCache.getAll();

            queries.forEach((query) => {
              const now = Date.now();
              const dataUpdatedAt = query.state.dataUpdatedAt || 0;
              const staleTime = query.queryKey.includes('Configuration')
                ? toMs(1, 'm')
                : toMs(5, 'm');
              const isStale = now - dataUpdatedAt > staleTime;

              if (isStale && !query.state.isFetching) {
                Logger.debug('refetching query', { queryKey: query.queryKey });
                queryClient.refetchQueries([query.queryKey]);
              }
            });
          }
        } else {
          handleFocus(false);
        }
      };
      const subscription = AppState.addEventListener('change', handler);

      return () => {
        subscription.remove();
      };
    });
  }

  instance = queryClient;

  return instance;
}
